import { useEffect } from 'react'
import baseLogger from '@/common/services/BaseLogger'

import DefaultLayout from '@/layouts/DefaultLayout'
import BaseHead from '@/components/common/BaseHead'
import useAmplitudeVisiblePage from '@/hooks/amplitude/useAmplitudeVisiblePage'
import { HOME_PAGE } from '@/common/constants/analytics'
import { userAmplitudeAnalytics } from '@/common/services/userAmplitudeAnalytics'
import { getCookie, setCookie } from 'cookies-next'
import { COOKIE_MAX_AGE } from '@/common/constants/duration'
import withTranslations from '@/hoc/withTranslations'
import {
  CONTROL,
  LANDINGS_CTA_TEXT_MAB_TEST,
} from '@/common/constants/amplitude'
import { wrapper } from '@/store'
import userLanguage from '@/common/services/userLanguage'
import { getExperimentKey } from '@/common/utils/getExperimentKey'
import LandingDefault from '@/components/landings/LandingDefault'

const anchors = [
  'hero',
  'efficiency',
  'exclusives',
  'possibilities',
  'search',
  'applications',
  'creative-work',
  'pricing',
  'socials',
]

const Landing = ({ experimentKey }) => {
  baseLogger.logger.info(
    `Running landing page MAB experiment – current variant: ${experimentKey}`
  )

  const calculatePercentScrolledPage = (id, anchors) => {
    const storagePercentScroll = getCookie('landing_seen') ?? 0
    const findIndex = anchors.findIndex((item) => item === id) + 1
    const percentScrolledLanding =
      ((findIndex - 1) / (anchors.length - 1)) * 100

    if (storagePercentScroll < percentScrolledLanding) {
      setCookie(
        'landing_seen',
        parseInt(percentScrolledLanding),
        COOKIE_MAX_AGE
      )

      userAmplitudeAnalytics.addUserProperties((event) => {
        event.set('landing_seen', parseInt(percentScrolledLanding))
      })
    }
  }

  const setHashForDispatchEvent = (hash) => {
    window.dispatchEvent(
      new HashChangeEvent('hashchange', {
        newURL: window.location.origin + window.location.pathname + hash,
        oldURL: window.location.href,
      })
    )
  }

  useEffect(() => {
    const handleScroll = async () => {
      try {
        const currentHash = localStorage.getItem('currentHash')
        if (currentHash) {
          const element = document.querySelector(currentHash)
          if (element) {
            setTimeout(() => {
              element.scrollIntoView({ behavior: 'smooth' })
              localStorage.setItem('currentHash', '')
            }, 100)
          }
        }
      } catch (e) {
        baseLogger.logger.error(e, 'failed access to localStorage')
      }

      for (const id of anchors) {
        const anchor = document.getElementById(id)
        if (anchor) {
          const rect = anchor.getBoundingClientRect()
          const viewportHeight = window.innerHeight
          const visibleHeight = Math.max(
            0,
            Math.min(rect.height, viewportHeight - rect.top, rect.bottom)
          )
          if (visibleHeight >= rect.height * 0.75) {
            const urlHash = window.location.hash.substring(1)

            if (id === 'hero') {
              window.history.replaceState(null, null, window.location.pathname)
              setHashForDispatchEvent('')
              return
            }
            if (urlHash !== id) {
              calculatePercentScrolledPage(id, anchors)
              window.history.replaceState(null, null, `#${id}`)
              setHashForDispatchEvent(`#${id}`)
            }
            break
          }
        }
      }
    }

    handleScroll()
    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [anchors, setHashForDispatchEvent])

  useEffect(() => {
    const storagePercentScroll = getCookie('landing_seen') ?? 0

    userAmplitudeAnalytics.addUserProperties((event) => {
      event.set('landing_seen', parseInt(storagePercentScroll))
    })
  }, [])

  useAmplitudeVisiblePage(HOME_PAGE)

  return (
    <DefaultLayout experimentKey={experimentKey}>
      <BaseHead />
      <LandingDefault experimentKey={experimentKey} />
    </DefaultLayout>
  )
}

export const getServerSideProps = withTranslations(
  '/',
  wrapper.getServerSideProps((store) => async ({ req }) => {
    const token = req.cookies.token
    const hash = req.cookies.hash

    if (token && hash) {
      return {
        redirect: {
          destination: `/${userLanguage.lang}/dashboard`,
          permanent: false,
        },
      }
    }

    const experimentKey = getExperimentKey(store, LANDINGS_CTA_TEXT_MAB_TEST)
    const correctKey =
      experimentKey === CONTROL ? `var1_${CONTROL}` : experimentKey

    return {
      props: {
        experimentKey: correctKey,
      },
    }
  })
)

export default Landing
